/**
 * Session backed by `GET /auth/session`. While the first probe is in flight the app shows a
 * skeleton shell (never a spinner, never a flash of the login page); a 401 means signed out
 * and RequireAuth sends the person to /login with their return path. Any later 401 from an
 * API call (session ended on another device, expired) drops the session the same way.
 */
import type { SessionResponse } from '@aroo/shared';
import { AppShell, Button, Skeleton } from '@mantine/core';
import { IconMapPinOff, IconPlugConnectedX } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { authKeys, endViewAs, getSession, logout, startViewAs } from '../api/auth';
import { ApiError, onUnauthorized } from '../api/client';
import { EmptyState } from '../components/EmptyState';
import { PageSkeleton } from '../components/PageSkeleton';
import { NO_ACCESS } from './access';
import { SessionContext, type Session } from './session';
import { accessOf, approverNameOf, scopeChoicesOf, userOf, viewingAsOf } from './sessionMapping';

const SIGNED_OUT_USER = {
  id: '',
  firstName: '',
  name: '',
  initials: '',
  homeBranch: '',
  homeSiteCode: null,
  roleTitle: '',
};

/** Shell-shaped loading state for the first session probe. */
function LoadingShell() {
  return (
    <AppShell header={{ height: 56 }} padding={0} withBorder={false}>
      <AppShell.Header style={{ background: 'var(--a-shell-bg, #111)' }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 16, height: 56, padding: '0 16px' }}
        >
          <Skeleton height={24} width={88} radius="sm" />
          <Skeleton height={20} width={140} radius="sm" />
          <span style={{ flex: 1 }} />
          <Skeleton circle height={32} width={32} />
        </div>
      </AppShell.Header>
      <AppShell.Main>
        <div style={{ padding: 24 }} aria-busy="true" aria-label="Signing you in">
          <PageSkeleton />
        </div>
      </AppShell.Main>
    </AppShell>
  );
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  // The chosen scope belongs to one person: a different session starts from the default again.
  const [scopeChoice, setScopeChoice] = useState<{ personId: string; scopeId: string } | null>(
    null,
  );

  const query = useQuery({
    queryKey: authKeys.session,
    queryFn: getSession,
    staleTime: 60_000,
    retry: 1,
    refetchOnWindowFocus: true,
  });

  const forget = useCallback(() => {
    // Drop everything that was loaded for the previous person, then mark signed out.
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
    qc.setQueryData(authKeys.session, null);
    setScopeChoice(null);
  }, [qc]);

  useEffect(() => {
    onUnauthorized(forget);
    return () => onUnauthorized(null);
  }, [forget]);

  const data = query.data ?? null;
  // The scope choice belongs to the subject: viewing as someone starts from their default.
  const personId = data ? (data.viewingAs?.personId ?? data.me.personId) : null;
  const scopeId = scopeChoice && scopeChoice.personId === personId ? scopeChoice.scopeId : null;

  /** Everything loaded so far was for the previous subject: start again from this session. */
  const replaceSession = useCallback(
    (next: SessionResponse) => {
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
      qc.setQueryData(authKeys.session, next);
      setScopeChoice(null);
    },
    [qc],
  );

  const session = useMemo<Session>(() => {
    const scopes = data ? scopeChoicesOf(data.access.scopes) : scopeChoicesOf([]);
    const scope = scopes.find((s) => s.id === scopeId) ?? scopes[0];
    return {
      signedIn: data !== null,
      user: data ? userOf(data) : SIGNED_OUT_USER,
      access: data ? accessOf(data) : NO_ACCESS,
      viewingAs: data ? viewingAsOf(data) : null,
      startViewAs: async (id: string) => {
        replaceSession(await startViewAs(id));
      },
      exitViewAs: async () => {
        replaceSession(await endViewAs());
      },
      approverName: data ? approverNameOf(data) : 'your manager',
      mustChangePassword: data?.me.account.mustChangePassword ?? false,
      scopes,
      scope,
      setScope: (id: string) => {
        if (personId) setScopeChoice({ personId, scopeId: id });
      },
      signIn: (next: SessionResponse) => {
        qc.setQueryData(authKeys.session, next);
      },
      signOut: async () => {
        try {
          await logout();
        } catch {
          // The server session may already be gone; forgetting locally is what matters.
        }
        forget();
      },
      refresh: async () => {
        await qc.invalidateQueries({ queryKey: authKeys.session });
      },
    };
  }, [data, scopeId, personId, qc, forget, replaceSession]);

  if (query.isPending) return <LoadingShell />;

  if (query.isError && query.data === undefined) {
    // Location policy (spec §3.5): the session is fine, the network isn't. It resumes on-site.
    if (query.error instanceof ApiError && query.error.code === 'off_site') {
      return (
        <div style={{ padding: 24, maxWidth: 560, margin: '48px auto' }}>
          <EmptyState
            variant="warn"
            icon={<IconMapPinOff size={28} stroke={2} />}
            title="Sign in from your branch"
            body="Your role only works from a Spartan site network. Your session is still there: it carries on when you're back on the branch Wi-Fi or a counter PC."
            action={
              <Button
                variant="default"
                onClick={() => {
                  void logout().catch(() => undefined);
                  forget();
                }}
              >
                Sign out
              </Button>
            }
          />
        </div>
      );
    }
    return (
      <div style={{ padding: 24, maxWidth: 560, margin: '48px auto' }}>
        <EmptyState
          variant="danger"
          icon={<IconPlugConnectedX size={28} stroke={2} />}
          title="Aroo can't reach the server"
          body="Check your connection and try again. If this keeps happening, tell the Aroo team."
          action={
            <Button variant="default" onClick={() => void query.refetch()}>
              Try again
            </Button>
          }
        />
      </div>
    );
  }

  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}
